import React, { useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import { Box, Button, Stack, Typography } from '@mui/material';

import { ALLOWED_IMAGE_UPLOAD_ACCEPT } from '~/services/ImageService';

import DeleteConfirmationDialog from '../../DeleteConfirmationDialog';
import ImportImagesDialog from '../OfferGallery/ImportImagesDialog';
import type { MediaImage, PropertyRoomTypeWithImages } from '../types';
import useImageDropUpload from '../useImageDropUpload';
import { UPLOAD_TILE_DRAG_LABEL, canPersistRoomImageHiddenState, uploadButtonDragActiveSx } from '../utils';

import RoomImageSlot from './Slot';
import SyncImagesButton from './SyncImagesButton';
import type { RoomImage, RoomImagesByKey } from './utils';

interface Props {
  hiddenImagePublicIds: Array<string>;
  isMetadataEditable?: boolean;
  mediaImages: Array<MediaImage>;
  selectedImagePublicIds: Array<string>;
  onEditImage: (publicId: string) => void;
  onImportRoomImages: (
    roomKey: string,
    photoUnits: Array<{ id_cloudinary_external: string; title?: string }>,
  ) => Promise<number>;
  onOpenImagesVideo?: () => void;
  onSyncTopRoomImages: (roomKey: string) => Promise<void>;
  onRemoveImageFromRoom: (image: MediaImage) => Promise<void> | void;
  onReorderRoomImages: (roomKey: string, orderedPublicIds: Array<string>) => Promise<void> | void;
  onToggleHiddenImage: (publicId: string) => Promise<void>;
  onToggleSelected: (publicId: string) => void;
  onUpdateImageTitle?: (roomKey: string, publicId: string, title: string) => void;
  onUploadImagesToRoom?: (roomId: string, roomName: string, files: Array<File>) => void;
  room: PropertyRoomTypeWithImages;
  roomImages: Array<RoomImage>;
  roomKey: string;
  setRoomImagesByKey: React.Dispatch<React.SetStateAction<RoomImagesByKey>>;
  showRoomDetails?: boolean;
}

export default function ImagesByRoomRow({
  hiddenImagePublicIds,
  isMetadataEditable = false,
  mediaImages,
  selectedImagePublicIds,
  onEditImage,
  onImportRoomImages,
  onOpenImagesVideo,
  onSyncTopRoomImages,
  onRemoveImageFromRoom,
  onReorderRoomImages,
  onToggleHiddenImage,
  onToggleSelected,
  onUpdateImageTitle,
  onUploadImagesToRoom,
  room,
  roomImages,
  roomKey,
  setRoomImagesByKey,
  showRoomDetails = true,
}: Props) {
  const [imagePendingDeletion, setImagePendingDeletion] = useState<MediaImage>();
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const roomName = room.name || 'Room';
  const canUploadDirectly = !!onUploadImagesToRoom;
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleConfirmDeleteImage = async () => {
    if (!imagePendingDeletion) {
      return;
    }

    try {
      await onRemoveImageFromRoom(imagePendingDeletion);
      setRoomImagesByKey((currentRoomImagesByKey) => ({
        ...currentRoomImagesByKey,
        [roomKey]: (currentRoomImagesByKey[roomKey] ?? []).filter(
          (image) => image.publicId !== imagePendingDeletion.publicId,
        ),
      }));
      setImagePendingDeletion(undefined);
    } catch {
      // Error already handled upstream.
    }
  };

  const handleDeleteImage = (publicId: string) => {
    const image =
      mediaImages.find((mediaImage) => mediaImage.publicId === publicId && mediaImage.sourceRoomId === roomKey) ??
      mediaImages.find((mediaImage) => mediaImage.publicId === publicId);

    if (image) {
      setImagePendingDeletion(image);
    }
  };

  const handleUpdateImageTitle = (publicId: string, title: string) => {
    setRoomImagesByKey((currentRoomImagesByKey) => ({
      ...currentRoomImagesByKey,
      [roomKey]: (currentRoomImagesByKey[roomKey] ?? []).map((image) =>
        image.publicId === publicId ? { ...image, title } : image,
      ),
    }));
    onUpdateImageTitle?.(roomKey, publicId, title);
  };

  const handleUploadImagesToRoom = (files: Array<File>) => {
    onUploadImagesToRoom?.(roomKey, roomName, files);
  };

  const { dragDropProps, isDragActive } = useImageDropUpload(canUploadDirectly ? handleUploadImagesToRoom : undefined);
  const addImagesLabel = isDragActive ? UPLOAD_TILE_DRAG_LABEL : 'Add images';

  const handleUploadInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);

    event.target.value = '';

    if (files.length === 0) {
      return;
    }

    handleUploadImagesToRoom(files);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = roomImages.findIndex((image) => image.publicId === active.id);
    const newIndex = roomImages.findIndex((image) => image.publicId === over.id);

    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    const nextRoomImages = arrayMove(roomImages, oldIndex, newIndex);

    setRoomImagesByKey((currentRoomImagesByKey) => ({
      ...currentRoomImagesByKey,
      [roomKey]: nextRoomImages,
    }));
    void onReorderRoomImages(
      roomKey,
      nextRoomImages.map((image) => image.publicId),
    );
  };

  return (
    <>
      <Box sx={{ py: showRoomDetails ? 3 : 0 }}>
        {!showRoomDetails && (
          <Stack spacing={2} sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Carousel order is used on the offer page. Drag images to reorder. Use the empty slot to upload an image.
              Hover an image to edit, hide, or delete.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', lg: 'auto' } }}>
              {canUploadDirectly && (
                <Button
                  component="label"
                  variant="text"
                  startIcon={<AddPhotoAlternateOutlinedIcon />}
                  {...dragDropProps}
                  sx={{
                    whiteSpace: 'nowrap',
                    ...(isDragActive ? uploadButtonDragActiveSx : undefined),
                  }}
                >
                  {addImagesLabel}
                  <input
                    hidden
                    accept={ALLOWED_IMAGE_UPLOAD_ACCEPT}
                    multiple
                    type="file"
                    onChange={handleUploadInputChange}
                  />
                </Button>
              )}
              <Button
                variant="text"
                startIcon={<PhotoLibraryOutlinedIcon />}
                sx={{ whiteSpace: 'nowrap' }}
                onClick={() => setIsImportDialogOpen(true)}
              >
                Import images
              </Button>
              <SyncImagesButton sx={{ whiteSpace: 'nowrap' }} onSync={() => onSyncTopRoomImages(roomKey)} />
              {onOpenImagesVideo && (
                <Button
                  variant="text"
                  startIcon={<OpenInNewIcon />}
                  onClick={onOpenImagesVideo}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Open Images & Video
                </Button>
              )}
            </Stack>
          </Stack>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: showRoomDetails ? { xs: '1fr', lg: '220px minmax(0, 1fr)' } : '1fr',
            gap: { xs: 2, lg: 3 },
          }}
        >
          {showRoomDetails && (
            <Stack spacing={1} alignItems="flex-start">
              <Box>
                <Typography variant="h6">{roomName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {roomImages.length} {roomImages.length === 1 ? 'image' : 'images'}
                </Typography>
              </Box>
              <Stack
                direction={{ xs: 'row', lg: 'column' }}
                spacing={{ xs: 2, lg: 0.5 }}
                alignItems="flex-start"
                flexWrap="wrap"
              >
                {canUploadDirectly && (
                  <Button
                    component="label"
                    variant="text"
                    startIcon={<AddPhotoAlternateOutlinedIcon />}
                    {...dragDropProps}
                    sx={{
                      px: { xs: 1, lg: 0 },
                      ...(isDragActive ? uploadButtonDragActiveSx : undefined),
                    }}
                  >
                    {addImagesLabel}
                    <input
                      hidden
                      accept={ALLOWED_IMAGE_UPLOAD_ACCEPT}
                      multiple
                      type="file"
                      onChange={handleUploadInputChange}
                    />
                  </Button>
                )}
                <Button
                  variant="text"
                  startIcon={<PhotoLibraryOutlinedIcon />}
                  sx={{ px: { xs: 1, lg: 0 } }}
                  onClick={() => setIsImportDialogOpen(true)}
                >
                  Import images
                </Button>
                <SyncImagesButton sx={{ px: { xs: 1, lg: 0 } }} onSync={() => onSyncTopRoomImages(roomKey)} />
              </Stack>
            </Stack>
          )}

          <DndContext onDragEnd={handleDragEnd} sensors={sensors} collisionDetection={closestCenter}>
            <Stack spacing={2}>
              <SortableContext items={roomImages.map((image) => image.publicId)} strategy={rectSortingStrategy}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(2, minmax(0, 1fr))',
                      md: 'repeat(3, minmax(0, 1fr))',
                    },
                    gap: 2,
                  }}
                >
                  {roomImages.map((image, index) => (
                    <RoomImageSlot
                      key={image.publicId}
                      canToggleHidden={mediaImages.some(
                        (mediaImage) =>
                          mediaImage.publicId === image.publicId && canPersistRoomImageHiddenState(mediaImage),
                      )}
                      hiddenImagePublicIds={hiddenImagePublicIds}
                      image={image}
                      isMetadataEditable={isMetadataEditable}
                      selectedImagePublicIds={selectedImagePublicIds}
                      onDeleteImage={handleDeleteImage}
                      onEditImage={onEditImage}
                      onToggleHiddenImage={onToggleHiddenImage}
                      onToggleSelected={onToggleSelected}
                      onUpdateImageTitle={handleUpdateImageTitle}
                      onUploadImages={canUploadDirectly ? handleUploadImagesToRoom : undefined}
                      slotNumber={index + 1}
                    />
                  ))}
                  <RoomImageSlot
                    hiddenImagePublicIds={hiddenImagePublicIds}
                    isMetadataEditable={isMetadataEditable}
                    selectedImagePublicIds={selectedImagePublicIds}
                    onDeleteImage={handleDeleteImage}
                    onEditImage={onEditImage}
                    onToggleHiddenImage={onToggleHiddenImage}
                    onToggleSelected={onToggleSelected}
                    onUpdateImageTitle={handleUpdateImageTitle}
                    onUploadImages={canUploadDirectly ? handleUploadImagesToRoom : undefined}
                    slotNumber={roomImages.length + 1}
                  />
                </Box>
              </SortableContext>
            </Stack>
          </DndContext>
        </Box>
      </Box>
      <DeleteConfirmationDialog
        body={`This will delete the image from ${roomName}. This action cannot be undone.`}
        open={!!imagePendingDeletion}
        title="Delete image from room?"
        onClose={() => setImagePendingDeletion(undefined)}
        onConfirm={handleConfirmDeleteImage}
      />
      <ImportImagesDialog
        existingImageIds={mediaImages.map((image) => image.publicId)}
        propertyId={undefined}
        scopedRoom={room}
        open={isImportDialogOpen}
        onClose={() => setIsImportDialogOpen(false)}
        onImport={(photoUnits) => onImportRoomImages(roomKey, photoUnits)}
      />
    </>
  );
}
